import {
  Box,
  Button,
  Card,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  Log,
  PendingMerchantUserAction,
  getMerchantUserWithActions,
  getPendingMerchantUsers,
} from "../../services/services";
import ListHead from "../table/ListHead";
import Scrollbar from "../table/Scrollbar";
import RejectPendingMerchantUser from "./Dialogs/RejectPendingMerchantUser";
import { applySortFilter } from "../../utils/tableFunctions";
import { StyledTableRow } from "../TableRow";
import { useLoadingSpinnerContext } from "../../context/loadingSpinnerContext";
import { CustomTableCell } from "../TableCell";
import CommonDialog from "../CustomDialog";
import AccountDetails from "../../pages/details/admin/MerchantUserDetails";
import CircularLoader from "../circularLoader";
import { styles } from "../../utils/styles";
import Pagination from "../Pagination";
import { pageNumber, rowsPerPage } from "../../utils/constants";

const TABLE_HEAD = [
  { id: "email", label: "Email" },
  { id: "business_entities", label: "Business Entities" },
  { id: "merchant_group", label: "Merchant Group" },
  { id: "action", label: "Action" },
  { id: "approve" },
];

const PendingAccount = ({ userRole }) => {
  const { setGlobalLoading } = useLoadingSpinnerContext();
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [accountList, setAccountList] = useState({ results: [], count: 0 });
  const [loaded, setLoaded] = useState(false);
  const [pendingDetails, setPendingDetails] = useState(null);

  const [pagination, setPagination] = useState({
    page: pageNumber,
    page_size: rowsPerPage,
  });

  // dialogs
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [selectedId, setSelectedId] = useState();

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const filteredList = applySortFilter(
    accountList?.results || [],
    order,
    orderBy
  );

  const isItemNotFound = filteredList.length === 0;

  const renderData = data => {
    const count = data?.count || 0;
    const results = data?.results || [];
    const newData = results.map(item => ({
      ...item,
      merchant_group: item.merchant_group?.name,
      business_entities: item.merchant_user_business_entity
      ?.map((item) => item.business_name)
      .join(", ")
    }));
    setAccountList({ results: newData, count });
  }

  const queryPage = (queryParams) => {
    setLoaded(false);
    getPendingMerchantUsers({
      setResponse: renderData,
      setLoaded: setLoaded,
      filter: {},
      queryParams: queryParams,
    });
    setPagination(queryParams);
  };

  const handleChangePage = (event, newPage) => {
    queryPage({
      page: newPage,
      page_size: pagination.page_size,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    queryPage({
      page: 1,
      page_size: parseInt(event.target.value, 10),
    });
  };

  useEffect(() => {
    queryPage(pagination);
  }, []);

  const handleCloseDialog = (shouldResetDetails) => {
    setOpenRejectDialog(false);
    if (shouldResetDetails === true) setPendingDetails(null);
  };
  return (
    <Box>
      {loaded ? (
        <Card>
          <Scrollbar>
            <TableContainer>
              <Table>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  onRequestSort={handleRequestSort}
                  checkbox={false}
                />

                <TableBody>
                  {filteredList.map((row) => {
                        const {
                          id,
                          email,
                          business_entities,
                          merchant_group,
                          action,
                        } = row;

                        const namesArray = business_entities?.split(", ") || [];
                        const displayNames = namesArray.length > 3 ? [...namesArray.slice(0, 3), "..."] : namesArray;

                        return (
                          <StyledTableRow
                            hover
                            key={id}
                          >
                            <CustomTableCell
                              field={email}
                            />
                            <CustomTableCell
                              field={
                                <Tooltip
                                  title={
                                    namesArray.length > 3 ? (
                                      <Box component="ul" sx={{ listStyle: "none", p: 1, m: 0 }}>
                                        {namesArray.map((name) => (
                                          <li key={name}>
                                            <Typography variant="body2">{name}</Typography>
                                          </li>
                                        ))}
                                      </Box>
                                    ) : ""
                                  }
                                  disableHoverListener={namesArray.length <= 3}
                                  arrow
                                >
                                  <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                                    {displayNames.map((name) => (
                                      <li key={name}>{name}</li>
                                    ))}
                                  </Box>
                                </Tooltip>
                              }
                            />
                            <CustomTableCell
                              field={merchant_group}
                            />
                            <CustomTableCell
                              field={action === "Remove" ? "Deactivate" : action === "Restore" ? "Activate" : action}
                            />
                            <TableCell>
                              <Button
                                variant="text"
                                color="secondary"
                                onClick={() => {
                                  setGlobalLoading(true);
                                  getMerchantUserWithActions(id, setPendingDetails, () => setGlobalLoading(false));
                                }}
                              >
                                View Detail
                              </Button>
                            </TableCell>
                          </StyledTableRow>
                        );
                      })}
                </TableBody>
                {isItemNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <Paper>
                          <Typography
                            gutterBottom
                            align="center"
                            variant="subtitle1"
                          >
                            There is no pending account at the moment.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          {!isItemNotFound && (
            <Pagination
              count={accountList?.count || 0}
              pagination={pagination}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Card>
      ) : (
        <CircularLoader />
      )}
      {selectedId && (
        <RejectPendingMerchantUser
          open={openRejectDialog}
          onClose={handleCloseDialog}
          selectedId={selectedId}
          setAccountList={renderData}
          setLoaded={setLoaded}
          pagination={pagination}
        />
      )}

      {pendingDetails && (
        <CommonDialog
          title="Merchant User Details"
          onClose={() => setPendingDetails(null)}
          renderActions={() =>
            userRole.includes("Approve/Reject") && (
              <DialogActions>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setOpenRejectDialog(true);
                    setSelectedId(pendingDetails.id);
                  }}
                >
                  Reject
                </Button>
                <Button
                  variant="contained"
                  onClick={async () => {
                    setGlobalLoading(true);
                    Log.info(`Click button [Approve] - Approve Merchant User by Id: ${pendingDetails.id}`)
                    await PendingMerchantUserAction(
                      pendingDetails.id,
                      "approve",
                      renderData,
                      setLoaded,
                      "",
                      pagination
                    )
                    setPendingDetails(null);
                    setGlobalLoading(false);
                  }}
                  sx={{ ...styles.customButton }}
                >
                  Approve
                </Button>
              </DialogActions>
            )
          }
        >
          <AccountDetails detailsData={pendingDetails} />
        </CommonDialog>
      )}
    </Box>
  );
};

export default PendingAccount;
